import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Bookmark, Copy, Archive, Search, Layers, Calendar, Grid3x3, FolderTree, Plus } from "lucide-react";

const SCOPE_META = {
  block: { label: "Bloque",  icon: Layers,     color: "#60a5fa" },
  day:   { label: "Sesión",  icon: Calendar,   color: "#facc15" },
  week:  { label: "Semana",  icon: Grid3x3,    color: "#4ade80" },
  plan:  { label: "Plan",    icon: FolderTree, color: "#c084fc" },
};

export default function AdminPlanTemplates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("");
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (scope) params.set("scope", scope);
      if (q) params.set("q", q);
      if (showArchived) params.set("include_archived", "true");
      const { data } = await api.get(`/admin/plan-templates?${params}`);
      setItems(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [scope, showArchived]); // eslint-disable-line

  const duplicate = async (id) => {
    try { await api.post(`/admin/plan-templates/${id}/duplicate`); toast.success("Duplicada"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const archive = async (id) => {
    if (!window.confirm("Archivar plantilla? Puedes verla con 'Mostrar archivadas'.")) return;
    try { await api.post(`/admin/plan-templates/${id}/archive`); toast.success("Archivada"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const grouped = items.reduce((a, t) => { a[t.scope] = a[t.scope] || []; a[t.scope].push(t); return a; }, {});

  return (
    <AppShell>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2rem", letterSpacing: "0.06em", margin: 0 }}>Plantillas de Plan V2</h1>
          <p style={{ color: "#a3a3a3", fontSize: 13, margin: "4px 0 0" }}>Bloques, sesiones, semanas y planes reutilizables. Al insertar se clonan — no quedan vinculados.</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 3, padding: "8px 12px", flex: "1 1 280px" }}>
          <Search size={14} color="#a3a3a3" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                 placeholder="Buscar plantilla..." style={{ background: "transparent", border: "none", color: "#fff", outline: "none", flex: 1, fontSize: 14 }}
                 data-testid="tmpl-search" />
        </div>
        <select value={scope} onChange={(e) => setScope(e.target.value)} style={selectStyle} data-testid="tmpl-scope-filter">
          <option value="">Todos los tipos</option>
          {Object.entries(SCOPE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#a3a3a3", cursor: "pointer" }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Ver archivadas
        </label>
      </div>

      {loading && <div style={{ padding: 40, textAlign: "center", color: "#a3a3a3" }}>Cargando…</div>}
      {!loading && items.length === 0 && (
        <div style={{ padding: 60, textAlign: "center", background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4 }}>
          <Bookmark size={40} color="#525252" style={{ marginBottom: 12 }} />
          <div style={{ color: "#a3a3a3", marginBottom: 6 }}>Aún no tienes plantillas.</div>
          <div style={{ color: "#737373", fontSize: 12 }}>Desde el editor de un plan V2 podrás guardar bloques, sesiones o semanas como plantilla.</div>
        </div>
      )}

      {!loading && Object.entries(grouped).map(([sc, list]) => {
        const m = SCOPE_META[sc] || { label: sc, icon: Bookmark, color: "#a3a3a3" };
        const Ic = m.icon;
        return (
          <div key={sc} style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: "Bebas Neue, sans-serif", letterSpacing: "0.14em", fontSize: 18, color: m.color, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
              <Ic size={16}/> {m.label.toUpperCase()} ({list.length})
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
              {list.map((t) => (
                <div key={t.id} className="qv-card-anim" style={{ background: "#0a0a0a", border: `1px solid ${t.archived_at ? "rgba(115,115,115,0.2)" : "rgba(255,255,255,0.06)"}`, padding: 14, borderRadius: 4, opacity: t.archived_at ? 0.55 : 1 }} data-testid={`tmpl-card-${t.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{t.name}</div>
                      {t.category && <div style={{ fontSize: 11, letterSpacing: "0.08em", color: m.color, textTransform: "uppercase", marginTop: 3 }}>{t.category}</div>}
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => duplicate(t.id)} style={iconBtn} title="Duplicar" data-testid={`tmpl-dup-${t.id}`}><Copy size={13}/></button>
                      {!t.archived_at && <button onClick={() => archive(t.id)} style={iconBtn} title="Archivar" data-testid={`tmpl-arch-${t.id}`}><Archive size={13}/></button>}
                    </div>
                  </div>
                  {t.description && <p style={{ fontSize: 12, color: "#a3a3a3", marginTop: 8 }}>{t.description}</p>}
                  <div style={{ fontSize: 10, color: "#737373", marginTop: 10, display: "flex", justifyContent: "space-between" }}>
                    <span>Usada {t.usage_count || 0} {t.usage_count === 1 ? "vez" : "veces"}</span>
                    <span>{new Date(t.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </AppShell>
  );
}

const selectStyle = { background: "#111", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "8px 12px", fontSize: 13, borderRadius: 3, outline: "none", minWidth: 160 };
const iconBtn = { background: "transparent", border: "none", color: "#a3a3a3", cursor: "pointer", padding: 4 };
